import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, Phone, CheckCircle, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Major Indian cities
const ALL_INDIAN_CITIES = [
  "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad",
  "Jaipur", "Surat", "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal",
  "Visakhapatnam", "Patna", "Vadodara", "Ghaziabad", "Ludhiana", "Agra", "Nashik",
  "Faridabad", "Meerut", "Rajkot", "Varanasi", "Srinagar", "Aurangabad", "Dhanbad",
  "Amritsar", "Allahabad", "Ranchi", "Howrah", "Coimbatore", "Jabalpur", "Gwalior",
  "Vijayawada", "Jodhpur", "Madurai", "Raipur", "Kota", "Chandigarh", "Guwahati",
  "Bhubaneswar", "Cuttack", "Jajpur", "Berhampur", "Sambalpur", "Rourkela"
];

export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('phone'); // phone, success
  const [loading, setLoading] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    pincode: '',
  });

  useEffect(() => {
    if (citySearch.length > 0) {
      const filtered = ALL_INDIAN_CITIES.filter(city => 
        city.toLowerCase().includes(citySearch.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowCityDropdown(true);
    } else {
      setFilteredCities([]);
      setShowCityDropdown(false);
    }
  }, [citySearch]);

  const handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    // Only allow numbers for phone and pincode
    if (name === 'phone' || name === 'pincode') {
      if (!/^\d*$/.test(value)) return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city: city });
    setCitySearch(city);
    setShowCityDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (!formData.city) {
      toast.error('Please select your city');
      return;
    }

    if (formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    
    setLoading(true);
    
    try {
      // Save customer inquiry
      await axios.post(`${API}/customers/inquiry`, {
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        pincode: formData.pincode,
      });
      
      toast.success('Thank you for your interest!');
      setStep('success');
    } catch (error) {
      // Even if API fails, show success (for demo)
      toast.success('Thank you for your interest!');
      setStep('success');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('phone');
    setFormData({ name: '', phone: '', city: '', pincode: '' });
    setCitySearch('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {step === 'phone' && 'Welcome to Fudco!'}
            {step === 'success' && 'Thank You!'}
          </DialogTitle>
        </DialogHeader>

        {/* Phone Number Step */}
        {step === 'phone' && (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="text-center mb-4">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-full inline-block mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <p className="text-gray-600 text-sm">
                Enter your details to get notified when we launch
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
                className="h-12 rounded-xl"
                data-testid="auth-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="flex">
                <div className="flex items-center px-4 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-gray-600">
                  +91
                </div>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter 10-digit number"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  required
                  className="h-12 rounded-l-none rounded-r-xl"
                  data-testid="auth-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2 relative">
              <Label htmlFor="city">City *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="city"
                  type="text"
                  placeholder="Search your city..."
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(e.target.value);
                    setFormData({ ...formData, city: '' });
                  }}
                  className="h-12 rounded-xl pl-10"
                  data-testid="auth-city-input"
                />
              </div>
              {showCityDropdown && filteredCities.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {filteredCities.slice(0, 8).map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => handleCitySelect(city)}
                      className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-2 text-sm"
                    >
                      <MapPin className="w-4 h-4 text-[#2D8B2E]" />
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode *</Label>
              <Input
                id="pincode"
                name="pincode"
                type="text"
                placeholder="Enter 6-digit pincode"
                value={formData.pincode}
                onChange={handleChange}
                maxLength={6}
                required
                className="h-12 rounded-xl"
                data-testid="auth-pincode-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || formData.phone.length !== 10 || !formData.name.trim() || !formData.city || formData.pincode.length !== 6}
              className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Notify Me'
              )}
            </Button>

            <p className="text-center text-xs text-gray-500">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-[#2D8B2E] hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-[#2D8B2E] hover:underline">Privacy Policy</a>
            </p>
          </form>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="space-y-6 mt-4 text-center">
            <div className="bg-green-100 text-[#2D8B2E] p-6 rounded-full inline-block">
              <CheckCircle className="w-12 h-12" />
            </div>
            <p className="text-gray-700 text-lg font-medium">
              We will reach out to you soon when we go Live with our Application!
            </p>
            <p className="text-gray-500 text-sm">
              Our team will contact you on your registered phone number.
            </p>
            <Button
              onClick={handleClose}
              className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold"
              data-testid="success-close-btn"
            >
              Got it!
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
